import React from "react";
import { Box, Chip, Paper, Typography } from "@mui/material";
import { ifasColors } from "../../components/OperationsUI";

/**
 * Shared report UI primitives that carry the approved IFAS design standard:
 * KPI cards (label + tinted icon + big value + caption) and colour-coded
 * status chips with a leading dot. Theme colours come from ifasColors;
 * green/amber/red/blue are semantic status tones.
 */

export type Tone = "orange" | "green" | "amber" | "red" | "blue" | "grey" | "ink";

const TONES: Record<Tone, { color: string; bg: string }> = {
  orange: { color: ifasColors.orange, bg: ifasColors.softOrange },
  green: { color: "#1B7A3D", bg: "#EAF7EF" },
  amber: { color: "#B45309", bg: "#FDF0DF" },
  red: { color: "#C62828", bg: "#FDECEC" },
  blue: { color: ifasColors.info, bg: "#EAF1FF" },
  grey: { color: "#5C6472", bg: "#EEF0F3" },
  ink: { color: ifasColors.text, bg: ifasColors.page },
};

/** Map the MUI colour names the report tabs already use to a design tone. */
export const toneFromMuiColor = (
  color?: "success" | "info" | "error" | "warning" | "primary" | "default" | string,
): Tone => {
  switch (color) {
    case "success": return "green";
    case "info": return "blue";
    case "error": return "red";
    case "warning": return "amber";
    case "primary": return "blue";
    default: return "grey";
  }
};

/** Turn a raw status enum (e.g. AWAITING_DOCUMENTS) into "Awaiting Documents". */
const humanizeStatus = (value: string) =>
  value.replace(/[_-]+/g, " ").trim().toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

/** Derive a sensible tone from a status string by keyword. */
export const autoStatusTone = (value: string): Tone => {
  const v = String(value || "").toUpperCase();
  if (/COMPLETE|CLOSED|PAID|APPROVED|RELEASED|DELIVERED|CONFIRMED|NORMAL|AVAILABLE|ISSUED|SIGNED/.test(v)) return "green";
  if (/CANCEL|DECLIN|REJECT|FAIL|OUT[_ ]?OF[_ ]?STOCK|OVERDUE|ERROR/.test(v)) return "red";
  if (/AWAIT|PENDING|PROGRESS|SCHEDULE|LOW[_ ]?STOCK|HOLD|REVIEW|REQUEST|TRANSFER|VERIFICATION|DRAFT/.test(v)) return "amber";
  if (/ACTIVE|NEW|OPEN|IN[_ ]?SERVICE|INTAKE|ALLOCATED|ASSIGNED|DISPATCH|EN[_ ]?ROUTE|OFFERED/.test(v)) return "blue";
  return "grey";
};

export function ReportKpiCard({
  label,
  value,
  caption,
  tone = "orange",
  icon,
}: {
  label: string;
  value: React.ReactNode;
  caption?: string;
  tone?: Tone;
  icon?: React.ReactNode;
}) {
  const t = TONES[tone];
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        height: "100%",
        borderRadius: "14px",
        border: `1px solid ${ifasColors.border}`,
        bgcolor: ifasColors.surface,
        boxShadow: "0 8px 24px rgba(15,23,42,0.045)",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
        <Typography sx={{ fontSize: "0.69rem", fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase", color: ifasColors.muted }}>
          {label}
        </Typography>
        {icon && (
          <Box sx={{ width: 30, height: 30, borderRadius: "8px", display: "grid", placeItems: "center", bgcolor: t.bg, color: t.color, "& .MuiSvgIcon-root": { fontSize: 18 } }}>
            {icon}
          </Box>
        )}
      </Box>
      <Typography sx={{ fontSize: "2rem", fontWeight: 800, letterSpacing: "-0.02em", mt: 1.25, mb: 0.25, lineHeight: 1, color: t.color }}>
        {value}
      </Typography>
      {caption && (
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: ifasColors.muted }}>{caption}</Typography>
      )}
    </Paper>
  );
}

export function ReportStatusChip({ label, tone }: { label: React.ReactNode; tone?: Tone }) {
  const isText = typeof label === "string";
  const display = isText ? humanizeStatus(label as string) : label;
  const t = TONES[tone ?? (isText ? autoStatusTone(label as string) : "grey")];
  return (
    <Chip
      size="small"
      label={
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.6 }}>
          <Box component="span" sx={{ width: 6, height: 6, borderRadius: "999px", bgcolor: "currentColor", flexShrink: 0 }} />
          {display}
        </Box>
      }
      sx={{ color: t.color, bgcolor: t.bg, fontWeight: 800, borderRadius: "8px" }}
    />
  );
}
